import type { RequestFn } from '../http';

/**
 * Kundenstammdaten — ServiceHub zuerst, weclapp danach (v1.385.0).
 *
 * Firmendaten stehen nach dem Speichern sofort im ServiceHub; weclapp bekommt sie über die
 * Warteliste `customer_weclapp_writes` (Erstversuch sofort, danach alle 5 Minuten).
 */

export interface CustomerMasterDataDto {
  customerId: string;
  customerNumber: string | null;
  weclappCustomerId: string | null;
  name: string;
  email: string | null;
  phone: string | null;
  website: string | null;
  vatId: string | null;
  weclapp: {
    /** In weclapp angelegt? */
    linked: boolean;
    /** Wartet auf weclapp — löst sich von allein. */
    waiting: number;
    /** Endgültig abgelehnt — jemand muss ran (Admin → Sync). */
    stuck: number;
    lastError: string | null;
  };
}

/** Nur geänderte Felder; `null` leert ein Feld (nicht den Firmennamen). */
export interface CustomerMasterDataInput {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  vatId?: string | null;
}

export interface CustomerWeclappWriteItem {
  id: string;
  customerId: string;
  customerName: string | null;
  customerNumber: string | null;
  weclappCustomerId: string | null;
  kind: 'CREATE_PARTY' | 'UPDATE_PARTY' | 'ADDRESS';
  status: 'PENDING' | 'SYNCED' | 'FAILED';
  summary: string;
  attempts: number;
  lastError: string | null;
  userName: string | null;
  createdAt: string;
}

export function createCustomerMasterDataApi(request: RequestFn) {
  return {
    /** `ref` = ServiceHub-ID (UUID) oder weclapp-ID. */
    async getCustomerMasterData(ref: string) {
      return request<CustomerMasterDataDto>(`/customers/${encodeURIComponent(ref)}/master-data`);
    },

    async updateCustomerMasterData(ref: string, data: CustomerMasterDataInput) {
      return request<CustomerMasterDataDto>(`/customers/${encodeURIComponent(ref)}/master-data`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
    },

    async pushCustomerMasterData(ref: string) {
      return request<CustomerMasterDataDto>(`/customers/${encodeURIComponent(ref)}/master-data/push`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },

    async getCustomerWeclappWrites() {
      return request<{ waiting: number; stuck: number; items: CustomerWeclappWriteItem[] }>(
        '/admin/customer-weclapp-writes',
      );
    },

    async retryCustomerWeclappWrite(id: string) {
      return request<{ status: string; lastError: string | null }>(
        `/admin/customer-weclapp-writes/${encodeURIComponent(id)}/retry`,
        { method: 'POST', body: JSON.stringify({}) },
      );
    },

    async acknowledgeCustomerWeclappWrite(id: string) {
      return request<{ ok: true }>(`/admin/customer-weclapp-writes/${encodeURIComponent(id)}/acknowledge`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
    },
  };
}
